import { Link } from 'react-router-dom';
import { parseDateSafe, formatDisplayShort } from '../../../lib/dateUtils';
import { assetDisplayLabel, buildingRoomDisplay } from '../../../lib/formatUtils';
import Button from '../../../components/ui/Button';

/** Null guard for display: returns empty string for null, blank, "NULL", or "undefined". */
function cleanDisplay(val) {
  if (val == null || val === '') return '';
  const s = String(val).trim();
  if (s.toUpperCase() === 'NULL' || s.toLowerCase() === 'undefined') return '';
  return s;
}

function locationDisplay(asset) {
  return buildingRoomDisplay(asset?.building, asset?.room);
}

function MobileCardSkeleton() {
  return (
    <div className="asset-mobile-card asset-mobile-card-skeleton">
      <div className="asset-mobile-card-field" />
      <div className="asset-mobile-card-field" />
      <div className="asset-mobile-card-field" />
      <div className="asset-mobile-card-field" />
      <div className="asset-mobile-card-actions" />
    </div>
  );
}

/**
 * Controlled mobile asset cards. All state, mutations, and data fetching stay
 * in the parent (Assets.jsx); this component only renders and forwards events
 * via props. Selection set logic is owned by the parent via onSelectId.
 */
export default function AssetMobileCards({
  assets,
  selectedIds,
  canManageAssets,
  snakeToTitleCase,
  showInitialSkeleton,
  showMoreSkeleton,
  onSelectId,
  onRecordTest,
  onEdit,
  onDelete,
}) {
  return (
    <div className="assets-mobile-cards">
      {showInitialSkeleton ? (
        Array.from({ length: 5 }).map((_, i) => (
          <MobileCardSkeleton key={`mobile-skeleton-${i}`} />
        ))
      ) : (
        (assets ?? []).map((asset) => {
          const nextTestDate = asset.next_test_date
            ? parseDateSafe(asset.next_test_date)
            : null;
          const isOverdue = nextTestDate && nextTestDate < new Date();
          return (
            <div
              key={asset.id}
              className={`asset-mobile-card asset-row-fade${selectedIds.has(asset.id) ? ' asset-row-selected' : ''}`}
            >
              {canManageAssets && (
                <label className="asset-mobile-checkbox-label">
                  <input
                    type="checkbox"
                    className="asset-row-checkbox"
                    checked={selectedIds.has(asset.id)}
                    onChange={(e) => onSelectId(asset.id, e.target.checked)}
                  />
                </label>
              )}
              <div className="asset-mobile-card-field">
                <span className="asset-mobile-label">Asset ID</span>
                <span className="asset-mobile-card-id">
                  {cleanDisplay(assetDisplayLabel(asset)) || cleanDisplay(snakeToTitleCase(asset.asset_type)) || '-'}
                </span>
              </div>
              <div className="asset-mobile-card-field">
                <span className="asset-mobile-label">Type</span>
                <span className="asset-mobile-card-type">
                  {cleanDisplay(snakeToTitleCase(asset.asset_type)) || '-'}
                  {asset.asset_type === 'workshop_equipment' && (
                    <span className="asset-type-badge asset-type-fitments" title="Fitments"> (Fitments)</span>
                  )}
                </span>
              </div>
              <div className="asset-mobile-card-field">
                <span className="asset-mobile-label">Location</span>
                <span className="asset-mobile-card-location">{locationDisplay(asset)}</span>
              </div>
              <div className="asset-mobile-card-field">
                <span className="asset-mobile-label">Next Test Due</span>
                <span className="asset-mobile-card-next">
                  {nextTestDate ? (
                    <span className={isOverdue ? 'next-test-overdue' : ''}>
                      {formatDisplayShort(nextTestDate)}
                    </span>
                  ) : '-'}
                </span>
              </div>
              <div className="asset-mobile-card-actions">
                <Button
                  type="button"
                  size="sm"
                  className="asset-mobile-btn min-h-[48px] h-12"
                  onClick={() => onRecordTest(asset)}
                >
                  Record Test
                </Button>
                <Link
                  to={`/tests?tab=history&assetId=${asset.id}`}
                  className="btn btn-sm asset-mobile-btn asset-mobile-btn-link asset-mobile-history-btn asset-history-btn min-h-[48px] h-12"
                >
                  History
                </Link>
                <Button
                  type="button"
                  size="sm"
                  className="asset-mobile-btn min-h-[48px] h-12"
                  onClick={() => onEdit(asset)}
                >
                  Edit
                </Button>
                {canManageAssets && (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    className="asset-mobile-btn"
                    onClick={() => onDelete(asset)}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>
          );
        })
      )}
      {showMoreSkeleton &&
        Array.from({ length: 3 }).map((_, i) => (
          <MobileCardSkeleton key={`mobile-more-${i}`} />
        ))}
    </div>
  );
}
